import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import StoreDetailsHeader from '../../../components/storeDetails/StoreDetailsHeader';
import StoreDetailsDisplay from '../../../components/storeDetails/StoreDetailsDisplay';
import EditStoreDialog from '../../../components/stores/EditStoreDialog';
import ReconciliationEmailDialog from '../../../components/storeDetails/ReconciliationEmailDialog';
import SelectDatePicker from '../../../components/audit/SelectDatePicker';
import StoreOnboardingTab from '../../../components/storeDetails/StoreOnboardingTab';
import StoreDetailsAuditsTab from '../audits/components/StoreDetailsAuditsTab';
import StoreDetailsManualEntriesTabContainer from '../manualEntries/components/StoreDetailsManualEntriesTabContainer';
import StoreDetailsSendNotificationDialog from '../notifications/components/StoreDetailsSendNotificationDialog';
import StoreDetailsNotificationsTab from '../notifications/components/StoreDetailsNotificationsTab';
import { useStoreDetailsNotifications } from '../notifications/hooks/useStoreDetailsNotifications';
import { getStoreDetailsNotificationRecipientUsers } from '../notifications/utils/storeDetailsNotificationUsers';
import StoreDetailsSalesReceiptsTab from '../salesReceipts/components/StoreDetailsSalesReceiptsTab';
import StoreDetailsUsersTab from '../users/components/StoreDetailsUsersTab';
import StoreDetailsAddUserDialog from '../users/components/StoreDetailsAddUserDialog';
import StoreDetailsStoreLocationsTab from '../storeLocations/components/StoreDetailsStoreLocationsTab';
import PackStockSimulatorsContent from '../../caseSimulator/components/PackStockSimulatorsContent';
import EmailPipelineContent from '../../emailPipeline/components/EmailPipelineContent';
import RemoteVnc from '../../../pages/RemoteVnc';
import { useStoreDetailsPageLogic } from '../hooks/useStoreDetailsPageLogic';
import { useStoreSwitcher } from '../hooks/useStoreSwitcher';
import AdminSurface from '../../../components/admin/AdminSurface';
import { adminColors, adminTypography } from '../../../styles/adminDesignTokens';
import {
  adminButtonSx,
  adminButtonToneSx,
  adminCombineSx,
  adminEmptyStateSx,
} from '../../../styles/adminComponentStyles';

const StoreDetailsPage: React.FC = () => {
  const { model, actions } = useStoreDetailsPageLogic();
  const location = useLocation();
  const storeSwitcherFilters = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      enabled: model.isSuperAdmin,
      search: params.get('search'),
      subscriptionStatus: params.get('status'),
    };
  }, [location.search, model.isSuperAdmin]);
  const { stores: storesForSwitcher } = useStoreSwitcher(storeSwitcherFilters);
  const handleStoreChange = React.useCallback((nextStoreId: string) => {
    if (!nextStoreId || nextStoreId === model.storeId) {
      return;
    }

    actions.navigate({
      pathname: `/dashboard/store-summary/stores/${nextStoreId}`,
      search: location.search || '',
    });
  }, [actions, location.search, model.storeId]);
  const notificationsState = useStoreDetailsNotifications({
    activeTab: model.activeTab,
    storeId: model.storeId,
  });
  const notificationRecipientUsers = React.useMemo(
    () => getStoreDetailsNotificationRecipientUsers(model.usersState.storeUsers),
    [model.usersState.storeUsers],
  );
  const isRemovingCurrentUser = model.usersState.hiddenManagerActionLabel === 'Remove me from store';

  if (!model.isSuperAdmin) {
    return (
      <Box sx={{ p: 2.5 }}>
        <AdminSurface sx={{ p: 4, textAlign: 'center', maxWidth: 600, mx: 'auto', mt: 8 }}>
          <Typography sx={{ ...adminTypography.pageTitle, color: adminColors.text, mb: 1 }}>
            Access Denied
          </Typography>
          <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText, mb: 3 }}>
            Super admin privileges required to view store details.
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => actions.navigate('/dashboard/store-summary')}
            sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary)}
          >
            Back to Store Summary
          </Button>
        </AdminSurface>
      </Box>
    );
  }

  return (
    <Box>
      <StoreDetailsHeader
        store={model.store}
        storeOwner={model.usersState.storeOwner}
        onEdit={actions.handleOpenEditDialog}
        onBack={actions.handleBack}
        tabs={model.tabs}
        activeTab={model.activeTab}
        onTabChange={actions.handleTabChange}
        enableStoreSwitcher
        storesForSwitcher={storesForSwitcher}
        activeStoreId={model.storeId}
        onStoreChange={handleStoreChange}
        globalActions={(
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="flex-end"
            flexWrap="wrap"
            useFlexGap
            sx={{ rowGap: 0.75 }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon icon="mage:email" width="18" height="18" />}
              sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary)}
              onClick={model.auditsState.openReconciliationDialog}
              disabled={!model.store?.id || model.auditsState.reconciliationSending}
            >
              {model.auditsState.reconciliationSending ? 'Sending...' : 'Send Reconciliation Email'}
            </Button>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' }, mx: 0.5 }} />
            <Button
              variant="text"
              size="small"
              onClick={model.usersState.handleHiddenManagerAction}
              disabled={!model.store?.id || model.usersState.hiddenManagerSubmitting}
              sx={adminCombineSx(
                adminButtonSx,
                isRemovingCurrentUser ? adminButtonToneSx.danger : adminButtonToneSx.secondary,
                {
                  backgroundColor: isRemovingCurrentUser ? 'transparent' : undefined,
                  borderColor: 'transparent',
                  color: isRemovingCurrentUser ? adminColors.danger : undefined,
                  minWidth: 0,
                  px: 0.75,
                  textDecoration: isRemovingCurrentUser ? 'none' : 'underline',
                  '&:hover': {
                    backgroundColor: isRemovingCurrentUser ? 'rgba(180, 35, 24, 0.08)' : undefined,
                    borderColor: 'transparent',
                    textDecoration: isRemovingCurrentUser ? 'none' : 'underline',
                  },
                },
              )}
              title={model.usersState.hiddenManagerActionTitle}
            >
              {model.usersState.hiddenManagerSubmitting ? 'Updating...' : model.usersState.hiddenManagerActionLabel}
            </Button>
          </Stack>
        )}
        rightActions={(
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="flex-end"
            flexWrap="wrap"
            useFlexGap
            sx={{ rowGap: 0.75 }}
          >
            {model.activeTab === 'audits' && (
              <SelectDatePicker
                value={model.auditsState.auditDateRange}
                onChange={model.auditsState.setAuditDateRange}
              />
            )}
          </Stack>
        )}
      />

      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          padding: '16px',
          boxShadow: '0px 3px 16px rgb(0 0 0 / 8%)',
          border: '1px solid #e0e0e0',
          ...(model.activeTab === 'users' || model.activeTab === 'notifications'
            ? { paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }
            : {}),
        }}
      >
        {model.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : model.error ? (
          <AdminSurface sx={{ p: 2 }}>
            <Typography sx={{ ...adminTypography.body, color: adminColors.danger, mb: 2 }}>
              {model.error}
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={actions.loadStore}
              sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary)}
            >
              Retry
            </Button>
          </AdminSurface>
        ) : model.store ? (
          <>
          {model.activeTab === 'onboarding' && (
            <StoreOnboardingTab
              storeId={model.storeId}
              isSuperAdmin={model.isSuperAdmin}
              shouldLoad={model.activeTab === 'onboarding'}
              storeName={model.store?.name}
              subscriptionStatus={model.store?.subscription_status}
              storeUsers={model.usersState.storeUsers}
              storeOwner={model.usersState.storeOwner}
            />
          )}

          {model.activeTab === 'overview' && (
            <StoreDetailsDisplay store={model.store} onSilentStoreRefresh={actions.loadStoreSilently} />
          )}

          {model.activeTab === 'audits' && (
            <StoreDetailsAuditsTab
              store={model.store}
              auditReports={model.auditsState.auditReports}
              auditReportsLoading={model.auditsState.auditReportsLoading}
              auditReportsError={model.auditsState.auditReportsError}
              onRefresh={model.auditsState.loadAuditReports}
              dateRangeLabel={model.auditsState.dateRangeLabel}
              auditDateRange={model.auditsState.auditDateRange}
              setAuditDateRange={model.auditsState.setAuditDateRange}
            />
          )}

          {model.activeTab === 'users' && (
            <StoreDetailsUsersTab
              users={model.usersState.storeUsers}
              loading={model.usersState.usersLoading}
              error={model.usersState.usersError}
              mutatingUserId={model.usersState.mutatingStoreUserId}
              removingUserId={model.usersState.removingStoreUserId}
              ownerUserId={model.store.owner_user_id}
              onRemoveUser={model.usersState.handleRemoveStoreUser}
              onToggleDailyReconciliationEmail={model.usersState.handleToggleDailyReconciliationEmail}
              onToggleVisibility={model.usersState.handleToggleStoreUserVisibility}
              onAddUser={model.usersState.handleOpenAddUserDialog}
            />
          )}

          {model.activeTab === 'manual_entries' && (
            <StoreDetailsManualEntriesTabContainer store={model.store} todayDate={model.todayDate} />
          )}

          {model.activeTab === 'store_locations' && (
            <StoreDetailsStoreLocationsTab storeId={model.store.id} />
          )}

          {model.activeTab === 'lotto_reco_console' && (
            model.store.lotto_reco_console_id ? (
              <RemoteVnc deviceIdOverride={model.store.lotto_reco_console_id} showBackButton={false} />
            ) : (
              <Box sx={adminEmptyStateSx}>
                <Typography className="AdminEmptyState-title">
                  Watchdog unavailable
                </Typography>
                <Typography className="AdminEmptyState-copy">
                  This store does not have a watchdog console mapped yet.
                </Typography>
              </Box>
            )
          )}

          {model.activeTab === 'case_simulator' && (
            <PackStockSimulatorsContent storeId={model.store.id} stateCode={model.store.state} />
          )}

          {model.activeTab === 'sales_receipts' && (
            <StoreDetailsSalesReceiptsTab storeId={model.store.id} />
          )}

          {model.activeTab === 'email_pipeline' && (
            <EmailPipelineContent storeId={model.store.id} lockStoreSelection />
          )}

          {model.activeTab === 'notifications' && (
            <StoreDetailsNotificationsTab
              notifications={notificationsState.notifications}
              notificationStatuses={notificationsState.notificationStatuses}
              loading={notificationsState.notificationsLoading}
              error={notificationsState.notificationsError}
              onRefresh={notificationsState.loadNotifications}
              onSendNotification={notificationsState.handleOpenSendNotificationDialog}
            />
          )}
          </>
        ) : null}
      </Box>

      <EditStoreDialog
        open={model.editDialogOpen}
        store={model.store}
        onClose={actions.handleCloseEditDialog}
        onSave={actions.handleSaveStore}
        loading={model.saving}
      />

      <ReconciliationEmailDialog
        open={model.auditsState.reconcileDialogOpen}
        onClose={model.auditsState.closeReconciliationDialog}
        reconcileDate={model.auditsState.reconcileDate}
        onChangeDate={model.auditsState.setReconcileDate}
        forceResend={model.auditsState.forceResend}
        onForceResendChange={model.auditsState.setForceResend}
        sending={model.auditsState.reconciliationSending}
        responseMessage={model.auditsState.reconciliationResponse}
        onSend={model.auditsState.handleSendReconciliationEmail}
        storeId={model.store?.id}
        onClearResponseMessage={model.auditsState.clearReconciliationResponse}
      />

      <StoreDetailsAddUserDialog
        open={model.usersState.addUserDialogOpen}
        control={model.usersState.control}
        setValue={model.usersState.setValue}
        submitting={model.usersState.inviteSubmitting}
        inviteIsValid={model.usersState.inviteIsValid}
        onClose={model.usersState.handleCloseAddUserDialog}
        onSubmit={model.usersState.handleInviteUser}
      />

      <StoreDetailsSendNotificationDialog
        open={notificationsState.sendNotificationDialogOpen}
        users={notificationRecipientUsers}
        values={notificationsState.sendNotificationValues}
        submitting={notificationsState.sendNotificationSubmitting}
        sendDisabled={notificationsState.sendNotificationDisabled}
        error={notificationsState.sendNotificationError}
        onClose={notificationsState.handleCloseSendNotificationDialog}
        onSend={notificationsState.handleSendNotification}
        onTitleChange={notificationsState.handleSendNotificationTitleChange}
        onMessageChange={notificationsState.handleSendNotificationMessageChange}
        onAuditIdChange={notificationsState.handleSendNotificationAuditIdChange}
        onCriticalChange={notificationsState.handleSendNotificationCriticalChange}
        onUserChange={notificationsState.handleSendNotificationUserChange}
        onChannelChange={notificationsState.handleSendNotificationChannelChange}
      />
    </Box>
  );
};

export default StoreDetailsPage;
